import { useReducer, useOptimistic, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Flex,
  useColorMode,
  useDisclosure,
} from '@chakra-ui/react'
import { toast } from 'sonner'
import {
  useFullscreen,
  useUserTier,
  useDefaultShareOptions,
  useShareOptionsSync,
  useEditorDrafts,
  useShareViewing,
  useShareCreation,
  useShareUpdate,
} from './hooks'
import { editorReducer, createInitialState } from './state'
import { EditorSidebar } from './EditorSidebar'
import { EditorHeader, type ViewMode } from './EditorHeader'
import { MarkdownEditor as MarkdownEditorPane } from './markdown'
import { EditorEmptyState } from './EditorEmptyState'
import { SettingsModal } from './SettingsModal'
import { ShareModal } from './ShareModal'
import { ShareCreatedModal } from './ShareCreatedModal'
import { getSharePasscode } from '@/lib/storage/local-storage'
import type { UsageInfo } from '@/lib/api/keys'
import { buildFullShareUrl, getShareExtension } from '@/lib/utils/share-url'

interface EditorProps {
  initialContent?: string
  initialFilename?: string
  // View mode props
  readOnly?: boolean
  hideSidebar?: boolean
  defaultFullscreen?: boolean
  defaultViewMode?: ViewMode
  onEdit?: () => void  // Called when user wants to edit in read-only mode
  // For viewing modifiable shares directly (from ViewSharePage)
  viewingShareIdProp?: string
  viewingShareMetaProp?: { isModifiable: boolean; managementToken?: string; burnAfterRead?: boolean; expiresAt?: number }
}

export function Editor({
  initialContent = '',
  initialFilename = 'untitled.md',
  readOnly = false,
  hideSidebar = false,
  defaultFullscreen = false,
  defaultViewMode = 'split',
  onEdit,
  viewingShareIdProp,
  viewingShareMetaProp,
}: EditorProps) {
  const navigate = useNavigate()
  const { colorMode } = useColorMode()
  const { isOpen: isSettingsOpen, onOpen: onSettingsOpen, onClose: onSettingsClose } = useDisclosure()
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure()
  const { isOpen: isCreatedOpen, onOpen: onCreatedOpen, onClose: onCreatedClose } = useDisclosure()

  // --- Reducer state ---
  const [state, dispatch] = useReducer(
    editorReducer,
    createInitialState({
      content: initialContent,
      filename: initialFilename,
      originalContent: initialContent,
      viewMode: readOnly ? 'preview' : defaultViewMode,
      sidebarCollapsed: hideSidebar,
      viewingShareId: viewingShareIdProp,
      viewingShareMeta: viewingShareMetaProp ?? null,
    })
  )

  // Optimistic state for save operations
  const [optimisticOriginalContent, setOptimisticOriginalContent] = useOptimistic(state.originalContent)
  const [storageUsage, setStorageUsage] = useState<UsageInfo | null>(null)

  // --- Self-contained hooks ---
  const { isFullscreen } = useFullscreen({ readOnly, defaultFullscreen })
  const { userTier, pendingShareAfterSignUp } = useUserTier({ onSettingsOpen })
  const defaults = useDefaultShareOptions()
  const { defaultSecurityOptions, defaultPasscodeOptions, defaultFormatOptions } = defaults

  // --- Draft lifecycle ---
  const {
    drafts,
    refreshDrafts,
    saveCurrentDraftNow,
    handleNewFile,
    handleSelectDraft,
    handleDeleteDraft,
    handleRenameDraft,
    handleContentLoaded,
    handleCloseFile,
  } = useEditorDrafts(state, dispatch, {
    readOnly,
    initialContent,
    initialFilename,
    defaultViewMode,
    viewingShareIdProp,
    viewingShareMetaProp,
  })

  // --- Share options sync ---
  useShareOptionsSync(state, dispatch, {
    isShareOpen,
    defaultSecurityOptions,
    defaultPasscodeOptions,
    defaultFormatOptions,
  })

  // --- Share viewing ---
  const { handleSelectFile, handleCloneShare } = useShareViewing(state, dispatch, {
    saveCurrentDraftNow,
    handleContentLoaded,
  })

  // --- Share creation ---
  const { handleCreateShare, handleCreatedClose } = useShareCreation(state, dispatch, {
    userTier,
    defaultViewMode,
    pendingShareAfterSignUp,
    onSettingsClose,
    onShareClose,
    onCreatedOpen,
    onCreatedClose,
    refreshDrafts,
  })

  // --- Share update ---
  const { handleUpdateShare } = useShareUpdate(state, dispatch, {
    setOptimisticOriginalContent,
  })

  // --- Derived values ---
  const isModified = state.content !== optimisticOriginalContent
  const hasContent = state.content.trim().length > 0
  const isLoading = state.loadingOperation !== null

  // --- Simple handlers ---

  const handleEdit = () => {
    if (onEdit) {
      onEdit()
    } else {
      navigate('/editor', { state: { prefillContent: state.content, prefillFilename: state.filename } })
    }
  }

  const handleEditModifiableShare = () => {
    if (state.viewingShareMeta?.isModifiable) {
      dispatch({ type: 'ENTER_EDIT_MODE' })
    }
  }

  const showSidebar = !hideSidebar && !readOnly

  return (
    <>
      <Flex
        className="editor-scrollbar"
        direction="row"
        position={isFullscreen ? 'fixed' : 'relative'}
        top={isFullscreen ? 0 : undefined}
        left={isFullscreen ? 0 : undefined}
        right={isFullscreen ? 0 : undefined}
        bottom={isFullscreen ? 0 : undefined}
        h={isFullscreen ? '100vh' : undefined}
        flex={isFullscreen ? undefined : 1}
        minH={0}
        w="100%"
        bg={colorMode === 'dark' ? 'stone.900' : 'stone.50'}
        zIndex={isFullscreen ? 1000 : undefined}
        overflow="hidden"
      >
        {/* Sidebar - only shown in edit mode */}
        {showSidebar && (
          <EditorSidebar
            onSelectFile={handleSelectFile}
            onNewFile={handleNewFile}
            onContentLoaded={handleContentLoaded}
            onCloneShare={handleCloneShare}
            selectedFileId={state.selectedFileId}
            isCollapsed={state.sidebarCollapsed}
            onToggleCollapse={() => dispatch({ type: 'TOGGLE_SIDEBAR' })}
            drafts={drafts}
            currentDraftId={state.currentDraftId}
            onSelectDraft={handleSelectDraft}
            onDeleteDraft={handleDeleteDraft}
            onRenameDraft={handleRenameDraft}
            onShareDraft={onShareOpen}
            onSettings={onSettingsOpen}
            onUsageChange={setStorageUsage}
          />
        )}

        {/* Main editor area */}
        {!readOnly && !state.currentDraftId && !state.viewingShareId ? (
          <EditorEmptyState
            onNewFile={handleNewFile}
            onContentLoaded={handleContentLoaded}
          />
        ) : (
          <Flex
            flex={1}
            direction="column"
            overflow="hidden"
            borderLeftWidth={0}
          >
            <EditorHeader
              filename={state.filename}
              onFilenameChange={readOnly ? undefined : (f) => dispatch({ type: 'SET_FILENAME', filename: f })}
              isModified={isModified}
              viewMode={state.viewMode}
              onViewModeChange={(m) => dispatch({ type: 'SET_VIEW_MODE', viewMode: m })}
              hasContent={hasContent}
              onEdit={readOnly ? handleEdit : undefined}
              readOnly={readOnly}
              fileType={state.currentFileType}
              isModifiable={state.viewingShareMeta?.isModifiable}
              onEditModifiable={handleEditModifiableShare}
              onUpdateShare={handleUpdateShare}
              isUpdatingShare={isLoading}
              onClose={readOnly ? undefined : handleCloseFile}
              format={state.formatOptions.format}
              onFormatChange={readOnly ? undefined : (newFormat) => dispatch({ type: 'SET_FORMAT_OPTIONS', options: { format: newFormat } })}
              burnAfterRead={state.viewingShareMeta?.burnAfterRead}
              expiresAt={state.viewingShareMeta?.expiresAt}
              hideViewToggle={!!state.viewingShareId && !state.viewingShareMeta?.isModifiable}
              shareUrl={state.viewingShareId
                ? buildFullShareUrl(state.viewingShareId, getShareExtension(state.filename), getSharePasscode(state.viewingShareId))
                : undefined}
              showRawView={state.showRawView}
              onToggleRawView={readOnly || (state.viewingShareId && !state.viewingShareMeta?.isModifiable) ? () => dispatch({ type: 'TOGGLE_RAW_VIEW' }) : undefined}
            />
            <MarkdownEditorPane
              content={state.content}
              onContentChange={readOnly ? undefined : (c) => dispatch({ type: 'SET_CONTENT', content: c })}
              viewMode={state.viewMode}
              readOnly={readOnly}
              storageUsage={storageUsage}
              showRawView={state.showRawView}
              format={state.formatOptions.format}
            />
          </Flex>
        )}
      </Flex>

      {/* Settings Modal - for default settings */}
      {!readOnly && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={onSettingsClose}
          securityOptions={defaultSecurityOptions}
          onSecurityOptionsChange={defaults.setDefaultSecurityOptions}
          passcodeOptions={defaultPasscodeOptions}
          onPasscodeOptionsChange={defaults.setDefaultPasscodeOptions}
          tier={userTier}
        />
      )}

      {/* Share Modal - for creating shares */}
      {!readOnly && (
        <ShareModal
          isOpen={isShareOpen}
          onClose={onShareClose}
          securityOptions={state.securityOptions}
          onSecurityOptionsChange={(opts) => dispatch({ type: 'SET_SECURITY_OPTIONS', options: opts })}
          passcodeOptions={state.passcodeOptions}
          onPasscodeOptionsChange={(opts) => dispatch({ type: 'SET_PASSCODE_OPTIONS', options: opts })}
          turnstileToken={state.turnstileToken}
          onTurnstileSuccess={(token) => dispatch({ type: 'SET_TURNSTILE_TOKEN', token })}
          onTurnstileError={() => {
            toast.error('Security check failed. Please try again.')
            dispatch({ type: 'SET_TURNSTILE_TOKEN', token: null })
          }}
          onTurnstileExpire={() => dispatch({ type: 'SET_TURNSTILE_TOKEN', token: null })}
          onShare={handleCreateShare}
          isLoading={isLoading}
          hasContent={hasContent}
          tier={userTier}
        />
      )}

      {/* Share Created Modal */}
      {state.createdShare && (
        <ShareCreatedModal
          isOpen={isCreatedOpen}
          onClose={handleCreatedClose}
          shareId={state.createdShare.shareId}
          passcode={state.createdShare.passcode}
          expiresAt={state.createdShare.expiresAt}
          isPublic={state.createdShare.isPublic}
          filename={state.createdShare.filename}
          format={state.createdShare.format}
          embedAllowed={userTier === 'pro'}
        />
      )}
    </>
  )
}
